import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLang } from "../i18n";
import { EASE } from "./Reveal";

// The signature interaction: one hub, seven connected dimensions.
// Desktop: radial diagram with hover. Mobile: tap-select chips (no hover on phones).
const NODES = [
  { key: "tax", x: 50, y: 8 },
  { key: "accounting", x: 82.8, y: 23.8 },
  { key: "assets", x: 91, y: 59.3 },
  { key: "estate", x: 68.2, y: 87.8 },
  { key: "business", x: 31.8, y: 87.8 },
  { key: "investments", x: 9, y: 59.3 },
  { key: "legacy", x: 17.2, y: 23.8 },
];

const LABELS = {
  en: {
    tax: "Tax",
    accounting: "Accounting",
    assets: "Assets",
    estate: "Estate",
    business: "Business",
    investments: "Investments",
    legacy: "Legacy",
  },
  es: {
    tax: "Impuestos",
    accounting: "Contabilidad",
    assets: "Activos",
    estate: "Patrimonio",
    business: "Negocio",
    investments: "Inversiones",
    legacy: "Legado",
  },
};

const StrategyDiagram = () => {
  const { lang, t } = useLang();
  const [active, setActive] = useState("business");
  const relations = t.home.strategy.relations;

  const panel = (
    <div className="border border-[#F4F0E8]/10 bg-[#1E1E1E] p-6 sm:p-10">
      <p className="eyebrow text-[#C6A46A]">{t.home.strategy.hint}</p>
      <h3 className="mt-4 text-xl font-light tracking-tight text-[#F4F0E8] sm:text-2xl">
        {LABELS[lang][active]}
      </h3>
      <div className="mt-6 space-y-3">
        {(relations[active] || []).map((r, i) => (
          <motion.div
            key={`${active}-${r}`}
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: i * 0.08 }}
            className="flex items-center gap-3 text-sm text-[#F4F0E8]/75"
          >
            <span className="text-[#C6A46A]">{LABELS[lang][active]}</span>
            <ArrowRight className="h-3.5 w-3.5 text-[#C6A46A]" />
            <span>{r}</span>
          </motion.div>
        ))}
      </div>
    </div>
  );

  return (
    <div data-testid="strategy-diagram">
      {/* Mobile: tap-select chips (phones have no hover) */}
      <div className="sm:hidden">
        <p className="mb-6 text-center text-[0.6rem] uppercase tracking-[0.24em] text-[#F4F0E8]/45">
          {t.home.strategy.hub.join("  ·  ")}
        </p>
        <div className="grid grid-cols-2 gap-2" role="tablist" aria-label={t.home.strategy.title}>
          {NODES.map((n) => (
            <button
              key={n.key}
              role="tab"
              aria-selected={active === n.key}
              onClick={() => setActive(n.key)}
              data-testid={`diagram-chip-${n.key}`}
              className={`min-h-[48px] border px-3 py-3 text-[0.62rem] uppercase tracking-[0.16em] transition-colors ${
                active === n.key
                  ? "border-[#C6A46A] bg-[#C6A46A] text-[#171717]"
                  : "border-[#F4F0E8]/20 text-[#F4F0E8]/80"
              }`}
            >
              {LABELS[lang][n.key]}
            </button>
          ))}
        </div>
        <div className="mt-6">{panel}</div>
      </div>

      {/* Tablet & desktop: radial diagram */}
      <div className="hidden grid-cols-1 items-center gap-12 sm:grid lg:grid-cols-[1.15fr_1fr]">
        <div className="relative mx-auto aspect-square w-full max-w-[560px]">
          <svg viewBox="0 0 600 600" className="absolute inset-0 h-full w-full" aria-hidden="true">
            <circle cx="300" cy="300" r="252" fill="none" stroke="rgba(244,240,232,0.08)" strokeWidth="1" />
            {NODES.map((n, i) => (
              <motion.line
                key={n.key}
                x1="300"
                y1="300"
                x2={n.x * 6}
                y2={n.y * 6}
                stroke={active === n.key ? "#C6A46A" : "rgba(198,164,106,0.28)"}
                strokeWidth={active === n.key ? 1.4 : 1}
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 1.1, delay: 0.15 + i * 0.08, ease: EASE }}
              />
            ))}
          </svg>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: EASE }}
            className="absolute left-1/2 top-1/2 flex h-[34%] w-[34%] -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full border border-[#C6A46A]/40 bg-[#1E1E1E] text-center"
          >
            {t.home.strategy.hub.map((h, i) => (
              <span key={i} className="text-[0.55rem] uppercase tracking-[0.2em] text-[#F4F0E8]/80 sm:text-[0.65rem]">
                {h}
              </span>
            ))}
          </motion.div>

          {NODES.map((n, i) => (
            <motion.button
              key={n.key}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 + i * 0.07 }}
              onMouseEnter={() => setActive(n.key)}
              onFocus={() => setActive(n.key)}
              onClick={() => setActive(n.key)}
              data-testid={`diagram-node-${n.key}`}
              aria-pressed={active === n.key}
              className={`absolute min-h-[44px] -translate-x-1/2 -translate-y-1/2 border px-2 py-1.5 text-[0.55rem] uppercase tracking-[0.16em] transition-colors duration-300 sm:px-3 sm:py-2 sm:text-[0.65rem] ${
                active === n.key
                  ? "border-[#C6A46A] bg-[#C6A46A] text-[#171717]"
                  : "border-[#F4F0E8]/20 bg-[#171717]/80 text-[#F4F0E8]/80 hover:border-[#C6A46A]/60 hover:text-[#C6A46A]"
              }`}
              style={{ left: `${n.x}%`, top: `${n.y}%` }}
            >
              {LABELS[lang][n.key]}
            </motion.button>
          ))}
        </div>
        {panel}
      </div>
    </div>
  );
};

export default StrategyDiagram;
